import React, { useEffect, useMemo, useState } from 'react';
import SubscriptionRequiredModal from '../../../components/ui/SubscriptionRequiredModal';
import { api } from '../../../lib/api';
import { isSubscriptionError } from '../../../lib/subscriptionGate';

type FileLink = { id: number; file_url: string; original_name: string };

export type SubjectResourceItem = {
  id: number | string;
  title: string;
  created_at: string;
  text?: string | null;
  files?: FileLink[] | null;
  has_quiz?: boolean;
  quiz_submission?: {
    submitted_at: string | null;
    score: number | null;
    total: number | null;
    duration_seconds: number | null;
  } | null;
};

interface Props {
  subjectName: string;
  countLabel: string;
  apiBase: string;
  items: SubjectResourceItem[];
  downloadKind: 'teme' | 'cursuri';
  renderSelectedActions?: (selected: SubjectResourceItem) => React.ReactNode;
}

const SubjectResourceDropdownCard: React.FC<Props> = ({ subjectName, countLabel, apiBase, items, downloadKind, renderSelectedActions }) => {
  const ordered = useMemo(() => {
    const list = (items || []).slice();
    list.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
    return list;
  }, [items]);

  const [selectedId, setSelectedId] = useState<string>(() => (ordered[0]?.id != null ? String(ordered[0].id) : ''));
  const [upgradeOpen, setUpgradeOpen] = useState(false);

  const selected = useMemo(() => ordered.find((x) => String(x.id) === String(selectedId)) || ordered[0] || null, [ordered, selectedId]);

  useEffect(() => {
    if (!ordered.length) {
      if (selectedId) setSelectedId('');
      return;
    }
    if (!ordered.some((x) => String(x.id) === String(selectedId))) {
      setSelectedId(String(ordered[0].id));
    }
  }, [ordered, selectedId]);

  const doDownload = async (fileId: number) => {
    try {
      const endpoint =
        downloadKind === 'teme'
          ? `/api/teme/fisiere/${encodeURIComponent(String(fileId))}/download`
          : `/api/cursuri/fisiere/${encodeURIComponent(String(fileId))}/download`;
      const resp = await api.get(endpoint);
      const urlPath = String((resp.data as any)?.url || '');
      const base = String((api.defaults.baseURL || window.location.origin) as any).replace(/\/$/, '');
      const fullUrl = urlPath ? `${base}${urlPath.startsWith('/') ? urlPath : `/${urlPath}`}` : '';
      if (fullUrl) window.open(fullUrl, '_blank', 'noopener,noreferrer');
    } catch (e: any) {
      if (isSubscriptionError(e)) {
        setUpgradeOpen(true);
        return;
      }
      alert(e?.response?.data?.detail || 'Nu am putut descărca fișierul.');
    }
  };

  return (
    <div className="border border-border rounded-lg p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-sm font-semibold text-foreground truncate">{subjectName || '—'}</div>
          <div className="text-xs text-muted-foreground mt-1">{countLabel}</div>
        </div>

        <div className="flex items-center gap-2">
          <select
            value={selectedId}
            onChange={(e) => setSelectedId(e.target.value)}
            className="w-full sm:w-[360px] px-3 py-2 text-sm border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            disabled={ordered.length <= 1}
          >
            {ordered.map((it) => (
              <option key={String(it.id)} value={String(it.id)}>
                {it.title}
              </option>
            ))}
          </select>
        </div>
      </div>

      {selected && (
        <div className="mt-3">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="text-sm font-semibold text-foreground">{selected.title}</div>
              <div className="text-xs text-muted-foreground mt-1">{new Date(selected.created_at).toLocaleString('ro-RO')}</div>
            </div>
            {renderSelectedActions ? <div className="shrink-0">{renderSelectedActions(selected)}</div> : null}
          </div>
          {selected.text && <div className="text-sm text-muted-foreground mt-2 whitespace-pre-line">{selected.text}</div>}

          {!!selected.files?.length && (
            <div className="mt-3">
              <div className="text-xs font-medium text-muted-foreground mb-1">Fișiere</div>
              <ul className="space-y-1">
                {selected.files.map((f, idx) => (
                  <li key={`${selected.id}-${idx}`} className="text-sm">
                    <button className="text-primary hover:underline" onClick={() => void doDownload(Number(f.id))}>
                      {f.original_name}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      <SubscriptionRequiredModal open={upgradeOpen} feature="materials_download" onClose={() => setUpgradeOpen(false)} />
    </div>
  );
};

export default SubjectResourceDropdownCard;
